import React from 'react';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import { Preview } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import AnimatedStreamingText from '../../../common/AnimatedStreamingText';

interface DocumentPreviewTabProps {
  streamingContent: string;
  isStreamingActive: boolean;
  showMarkdownPreview: boolean;
  markdownContent: string;
}

export const DocumentPreviewTab: React.FC<DocumentPreviewTabProps> = ({
  streamingContent,
  isStreamingActive,
  showMarkdownPreview,
  markdownContent,
}) => {
  const markdownPreviewStyles = {
    p: 3,
    maxHeight: '400px',
    overflowY: 'auto',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      color: '#1f2937',
      fontWeight: 600,
      mt: 2,
      mb: 1
    },
    '& p': {
      color: '#374151',
      lineHeight: 1.6,
      mb: 1.5
    },
    '& ul, & ol': {
      color: '#374151',
      pl: 2,
      mb: 1.5
    },
    '& li': {
      mb: 0.5
    },
    '& code': {
      backgroundColor: '#f1f5f9',
      color: '#1e293b',
      padding: '2px 4px',
      borderRadius: 3,
      fontSize: '0.875em'
    },
    '& pre': {
      backgroundColor: '#f8fafc',
      borderRadius: 1,
      p: 2,
      overflow: 'auto',
      border: '1px solid #e2e8f0'
    },
    '& blockquote': {
      borderLeft: '4px solid #e5e7eb',
      pl: 2,
      ml: 0,
      fontStyle: 'italic',
      color: '#6b7280'
    }
  } as const;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        bgcolor: 'white',
        borderRadius: 1,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid #e5e7eb',
          bgcolor: '#f9fafb'
        }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#374151', 
              fontWeight: 600,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Preview sx={{ fontSize: '1.125rem' }} />
            Document Preview
            {isStreamingActive && (
              <Chip
                label="Building Live"
                size="small"
                sx={{
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  height: 18,
                  border: '1px solid #bbf7d0'
                }}
              />
            )}
            {!isStreamingActive && streamingContent && (
              <Chip
                label="Complete"
                size="small"
                sx={{
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  fontWeight: 500,
                  fontSize: '0.65rem',
                  height: 18,
                  border: '1px solid #93c5fd'
                }}
              />
            )}
          </Typography>
        </Box>
        <Box sx={streamingContent && isStreamingActive ? { p: 3 } : markdownPreviewStyles}>
          {/* Show streaming content if available */}
          {streamingContent && isStreamingActive ? (
            <AnimatedStreamingText
              text={streamingContent}
              isStreaming
              px={0}
              py={0}
              maxHeight={400}
              fontSize="1rem"
              lineHeight={1.6}
              color="#374151"
            />
          ) : streamingContent ? (
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
              {streamingContent}
            </ReactMarkdown>
          ) : showMarkdownPreview && markdownContent ? (
            /* Fallback to old markdown preview for backward compatibility */
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
              {markdownContent}
            </ReactMarkdown>
          ) : (
            /* Loading state */
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#6366f1', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Building document in real-time...
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Content will appear here as it's generated
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}; 